'use client'

import { useLayoutEffect, useRef, useState, useEffect, useSyncExternalStore, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { TutorQa, type TutorQaHandle } from '@/components/tutor-qa'
import { TutorBrandLabel } from '@/components/tutor-brand-label'
import { GuestTutorPrompt, type GuestReason } from '@/components/guest-tutor-prompt'
import { FeedbackForm } from '@/components/feedback-form'
import type { FeedbackKind } from '@/lib/feedback'
import { WordLookup, editableTarget, findReplyField, type WordLookupHandle } from '@/components/word-lookup'
import { isVoiceInputSupported } from '@/lib/use-voice-input'
import { useCoarsePointer } from '@/lib/use-coarse-pointer'
import { useDisplayPrefs } from '@/lib/display-prefs'
import {
  CARD_WEIGHT,
  FAB_GAP,
  FAB_MARGIN,
  INPUT_WEIGHT,
  TEXT_WEIGHT,
  chooseFabSpot,
  fabCandidates,
  isWideLink,
  overlapArea,
  type Box,
  type Obstacle,
  type Spot,
} from '@/lib/fab-placement'

// Скрыт на лендинге и во всём auth-флоу (вход/регистрация/проверка почты/
// ошибка входа) — там либо нет залогиненного пользователя (вопрос
// репетитору не отправится), либо это не «во время обучения» в принципе.
// Везде в остальном приложении, включая дашборд (решение пользователя
// 2026-09-18: раньше там была встроенная кнопка, теперь единая плавающая с
// «умным» размещением — useSmartPlacement ниже), — показан.
export const OPEN_FEEDBACK_EVENT = 'tk:open-feedback'

function isHiddenOn(pathname: string): boolean {
  // /travel/phrases, /life/phrases — публичные разговорники: гостю «Спроси Turkıdo»
  // недоступен (server action требует входа), кнопка была бы обманкой.
  // /consent — согласия на передачу реплик в AI ещё нет.
  // /onboarding — новичку до первого урока репетитор не нужен, а шар вставал на заголовок (решение 08.10).
  // /listen/numbers — упражнение на слух со своей клавиатурой цифр: репетитор там не нужен,
  // а шар ездил по экрану при каждом ответе и закрывал переключатели (решение 19.10).
  // /words/flow — поток слов идёт сам, шар ложился бы на кнопки "Знаю" и "Ещё раз" (26.10).
  return pathname === '/' || pathname.startsWith('/auth') || pathname.startsWith('/travel/phrases') || pathname.startsWith('/life/phrases') || pathname.startsWith('/consent') || pathname.startsWith('/onboarding') || pathname.startsWith('/listen/numbers') || pathname.startsWith('/words/flow')
}

// Версия в имени файла — принудительно перекачивает картинку в браузерах,
// у которых по старому URL ещё закэширован прежний логотип. Query-строку
// сюда добавлять нельзя: без явного `images.localPatterns` в next.config
// Next.js отклоняет локальные изображения с query-параметрами (400). Поднимать
// суффикс версии при каждой смене файла логотипа.
const LOGO_SRC = '/brand/logo-orb-v2.png'

// Анимация «вылета» модалки прямо из кнопки (FLIP): считаем смещение между
// центром кнопки и центром панели в реальных экранных координатах и в
// момент открытия/закрытия проигрываем translate+scale от одного к другому.
// Управляется через ref/DOM напрямую (а не через React state) — это чисто
// переходная, самоотменяющаяся анимация, ей не нужен ре-рендер.
const OPEN_MS = 360
const CLOSE_MS = 280

// Порог долгого нажатия: держим кнопку дольше этого — «Как сказать?»
// (word-lookup.tsx: говоришь русское слово, получаешь турецкое, окно не
// открывается; решение пользователя 2026-09-24 — раньше удержание сразу
// записывало вопрос репетитору). Отпустили раньше — обычный тап, открыть окно.
const LONG_PRESS_MS = 400

// Периодическая подсказка у кнопки (тач-устройства не знают hover — без
// этого про кнопку легко забыть). Показывается на HINT_SHOW_MS раз в
// HINT_CYCLE_MS, пока модалка закрыта.
const HINT_SHOW_MS = 2600
const HINT_CYCLE_MS = 9000

// Подсказка про удержание (пользователь 2026-09-27: «легко информировало, но не
// напрягало»): только на тач-устройствах, один раз за занятие (sessionStorage:
// до закрытия вкладки или приложения), через HOLD_HINT_DELAY_MS после загрузки
// на HOLD_HINT_SHOW_MS. Воспользовались удержанием - подсказка сразу пропадает
// до конца занятия; в следующее занятие покажется снова.
const HOLD_HINT_DELAY_MS = 1500
const HOLD_HINT_SHOW_MS = 4500
const HOLD_HINT_SESSION_KEY = 'tk-hold-hint-shown'

// На телефоне окно — во весь экран. Раскладка задаётся inline по matchMedia,
// а не классами Tailwind: мобильный Safari пользователя уже держал в кэше
// старый CSS-бандл dev-сервера (см. PROJECT.md, календарь /stats).
const MOBILE_QUERY = '(max-width: 767px)'
// Видимая высота окна, ниже которой шар на телефоне прячется за край.
const FAB_LOW_VIEWPORT_PX = 480
function subscribeMobile(cb: () => void) {
  const mq = window.matchMedia(MOBILE_QUERY)
  mq.addEventListener('change', cb)
  return () => mq.removeEventListener('change', cb)
}
function useIsMobile(): boolean {
  return useSyncExternalStore(subscribeMobile, () => window.matchMedia(MOBILE_QUERY).matches, () => false)
}

// Hover-подсказка у плавающей кнопки нужна только десктопу с мышью. На
// телефоне её нет в DOM вообще: при устаревшем CSS-бандле в кэше Safari (без
// .tk-fab-tip) её текст вываливался прямо в круглую кнопку и уезжал за экран.
const DESKTOP_HOVER_QUERY = '(min-width: 768px) and (hover: hover) and (pointer: fine)'
function subscribeDesktopHover(cb: () => void) {
  const mq = window.matchMedia(DESKTOP_HOVER_QUERY)
  mq.addEventListener('change', cb)
  return () => mq.removeEventListener('change', cb)
}
function useDesktopHover(): boolean {
  return useSyncExternalStore(subscribeDesktopHover, () => window.matchMedia(DESKTOP_HOVER_QUERY).matches, () => false)
}

// Видимая область над экранной клавиатурой. В iOS Safari клавиатура НЕ
// уменьшает layout viewport: `position: fixed; inset: 0` остаётся на всю
// высоту экрана, и нижняя часть окна (поле ввода) оказывается под клавиатурой.
// window.visualViewport даёт реальную видимую высоту и смещение — по ним
// оверлей и подгоняется. Пересчёт на resize (клавиатура открылась/закрылась)
// и scroll (Safari подкручивает страницу при фокусе в поле).
type ViewportBox = { top: number; height: number }
function useVisualViewportBox(active: boolean): ViewportBox | null {
  const [box, setBox] = useState<ViewportBox | null>(null)
  useEffect(() => {
    const vv = window.visualViewport
    if (!active || !vv) return
    const update = () => setBox({ top: vv.offsetTop, height: vv.height })
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      setBox(null)
    }
  }, [active])
  return box
}


// «Умное» место плавающей кнопки (просьба пользователя 2026-09-18): кнопка
// не должна закрывать задание, плашки и кнопки страницы. Кандидаты и выбор
// места - в lib/fab-placement.ts (там же правила и история решений), здесь
// только замер помех в видимой области. Пересчёт - по скроллу, ресайзу и
// изменениям DOM (появилась карточка ответа, раскрылась шпаргалка), не чаще
// кадра. Перемещение плавное (см. .tk-fab[data-placed] в globals.css).
// Элементы задания: шар их не закрывает, если есть хоть одно место без них
// (тикет 82). Кнопки внутри колонки шага (.tk-step-shell: урок, тренажёры,
// повторение, игры) - тоже задание: "Дальше", "Проверить", "Начать проверку".
const TASK_SELECTOR =
  '[data-fab-avoid], input, textarea, select, .tk-option, .tk-chunk-token, .tk-chunk-slot, .tk-letter-key, .tk-match-pill, .tk-reveal-btn, .tk-check-btn, .tk-continue-btn, .tk-step-shell button, .tk-step-shell [role="button"]'
// Прочие помехи: ссылки, кнопки, шапка, таблицы, карточки-подложки.
const OBSTACLE_SELECTOR = `${TASK_SELECTOR}, a, button, summary, [role="button"], header, .tk-vocab-card, .tk-word-card, .tk-bubble, table, .tk-grammar-table`
// Текст внутри ссылок и кнопок тоже считаем: плитка каталога (/grammar,
// /games, дашборд) или строка справочника на телефоне во всю ширину, и
// без строк текста шару было всё равно, лечь на подпись или на пустой край
// у стрелки (тикет 89).
const TEXT_SKIP = 'input, textarea, select, .tk-fab, [data-tutor-panel], script, style, noscript'
function isSoftCard(n: HTMLElement): boolean {
  return (n.classList.contains('tk-word-card') || n.classList.contains('tk-vocab-card')) && !n.matches('a, button, summary, [role="button"], [data-fab-avoid]')
}
function isVisible(n: Element): boolean {
  return typeof n.checkVisibility !== 'function' || n.checkVisibility()
}
// Видимая часть элемента внутри областей прокрутки. В тренажёре на телефоне
// листается только область задания (.tk-trainer-scroll, тикеты 83 и 84):
// разбор и поле, уехавшие за её край, обрезаны, но прямоугольники у них есть,
// и без обрезки полоса под подвалом с кнопками казалась занятой.
type ClipBox = { left: number; top: number; right: number; bottom: number }
function makeClipper() {
  const cache = new Map<Element, ClipBox | null>()
  const clipOf = (el: Element): ClipBox | null => {
    const parent = el.parentElement
    if (!parent || parent === document.body || parent === document.documentElement) return null
    if (cache.has(parent)) return cache.get(parent) ?? null
    let clip = clipOf(parent)
    const cs = getComputedStyle(parent)
    if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
      const r = parent.getBoundingClientRect()
      clip = clip
        ? { left: Math.max(clip.left, r.left), top: Math.max(clip.top, r.top), right: Math.min(clip.right, r.right), bottom: Math.min(clip.bottom, r.bottom) }
        : { left: r.left, top: r.top, right: r.right, bottom: r.bottom }
    }
    cache.set(parent, clip)
    return clip
  }
  return (el: Element, r: ClipBox): ClipBox => {
    const c = clipOf(el)
    return c ? { left: Math.max(c.left, r.left), top: Math.max(c.top, r.top), right: Math.min(c.right, r.right), bottom: Math.min(c.bottom, r.bottom) } : r
  }
}
function collectObstacles(fab: HTMLElement, vw: number, vh: number): Obstacle[] {
  const out: Obstacle[] = []
  const clip = makeClipper()
  const inView = (r: ClipBox) => r.right - r.left > 0 && r.bottom - r.top > 0 && r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw
  const fabW = fab.getBoundingClientRect().width
  for (const n of document.querySelectorAll<HTMLElement>(OBSTACLE_SELECTOR)) {
    if (fab.contains(n) || n.closest('.tk-fab')) continue
    // Содержимое закрытого <details> (разбор правила в тренажёре, таблицы
    // окончаний) браузер не показывает, но прямоугольники у него есть:
    // из-за них весь низ экрана считался занятым, и кнопка стояла посреди
    // задания (владелец 2026-10-02). Невидимое помехой не считаем.
    if (!isVisible(n)) continue
    const r = clip(n, n.getBoundingClientRect())
    if (!inView(r)) continue
    const task = n.matches(TASK_SELECTOR)
    // Широкая ссылка или строка справочника - подложка, текст на ней ниже.
    const soft = !task && (isSoftCard(n) || (n.matches('a, summary') && isWideLink(r.right - r.left, fabW)))
    const weight = n.tagName === 'INPUT' || n.tagName === 'TEXTAREA' ? INPUT_WEIGHT : soft ? CARD_WEIGHT : 1
    out.push({ left: r.left - FAB_GAP, top: r.top - FAB_GAP, right: r.right + FAB_GAP, bottom: r.bottom + FAB_GAP, kind: task ? 'task' : soft ? 'soft' : 'control', weight, pad: FAB_GAP })
  }
  // Строки текста (по строкам, а не по блоку: у короткой строки правый край
  // свободен). Без них шар на /grammar/oshibki вставал на вступление.
  // Ветки за пределами экрана пропускаем целиком: на длинных страницах
  // (словарь, урок) обход всех текстовых узлов на каждый пересчёт дорог.
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.nodeType === Node.TEXT_NODE) return node.nodeValue && node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
      const e = node as Element
      if (e.matches(TEXT_SKIP)) return NodeFilter.FILTER_REJECT
      const r = e.getBoundingClientRect()
      if (r.width > 0 && r.height > 0 && (r.bottom < 0 || r.top > vh)) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_SKIP
    },
  })
  const range = document.createRange()
  for (let t = walker.nextNode(); t; t = walker.nextNode()) {
    const parent = t.parentElement
    if (!parent || !isVisible(parent)) continue
    range.selectNodeContents(t)
    for (const raw of range.getClientRects()) {
      const r = clip(parent, raw)
      if (!inView(r)) continue
      out.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom, kind: 'soft', weight: TEXT_WEIGHT })
    }
  }
  return out
}
// Поле ввода в фокусе (набирают ответ, открыта клавиатура) — на телефоне
// кнопка уезжает за правый край целиком, пока фокус не уйдёт.
function typingNow(): boolean {
  const a = document.activeElement as HTMLElement | null
  if (!a || a.closest('.tk-fab, [data-tutor-panel]')) return false
  // Поле со своей клавиатурой на экране (inputMode="none", цифры и буквы
  // кнопками): системная клавиатура не открывается, прятать шар незачем.
  // Иначе он уезжал при фокусе в поле и возвращался при нажатии клавиши (19.10).
  if (a.inputMode === 'none') return false
  if (a.isContentEditable || a.tagName === 'TEXTAREA') return true
  return a.tagName === 'INPUT' && /^(text|search|email|url|tel|number|password|)$/.test((a as HTMLInputElement).type)
}
// Периодическая подпись у кнопки ложилась на «Продолжить» и другие кнопки
// шага (тикет 67): размещение кнопки подпись не учитывает. Перед показом
// меряем, где она встанет (скрытая подпись уже стоит на месте, только
// прозрачная), и если накроет кнопку, ссылку, поле или вариант задания, этот
// цикл пропускаем. Уже показанную подпись прячем, если шаг сменился и под ней
// оказалась кнопка ("Начать проверку", тикет 82).
// Кнопка шага справа внизу (урок, тренажёры, разминки, игры; 15.10.2026): шар
// стоит слева от неё в той же строке, и подписи между ними места нет (меньше
// 90 px). Проверка по прямоугольникам на Android в "Крупном" ошибалась (шар со
// своим zoom внутри zoom страницы), и "Спроси Turkido" ложилась на "Следующая
// реплика" (владелец 15.10.2026). Поэтому на телефоне при такой кнопке подпись
// не показываем совсем, без измерений.
const BOTTOM_STEP_BTN = '.tk-lesson-shell > .tk-continue-btn, .tk-next-btn'
function bottomStepButtonShown(): boolean {
  if (!window.matchMedia(MOBILE_QUERY).matches) return false
  for (const n of document.querySelectorAll<HTMLElement>(BOTTOM_STEP_BTN)) if (isVisible(n)) return true
  return false
}
// Экран с заданием: шаг урока (и его итоговые экраны), повторение, тренажёр,
// чат, а также любая кнопка шага внизу. Здесь шар убирается, если ученик
// выключил его в настройках ("Шар в уроках", тикет 119).
function stepScreenShown(): boolean {
  for (const n of document.querySelectorAll<HTMLElement>(`.tk-step-shell, ${BOTTOM_STEP_BTN}`)) if (isVisible(n)) return true
  return false
}
function hintCoversControls(hint: HTMLElement | null): boolean {
  if (!hint) return false
  if (bottomStepButtonShown()) return true
  const r = hint.getBoundingClientRect()
  if (r.width === 0 || r.height === 0) return false
  const box: Box = { left: r.left, top: r.top, right: r.right, bottom: r.bottom }
  for (const n of document.querySelectorAll<HTMLElement>(`${TASK_SELECTOR}, a, button, summary, [role="button"]`)) {
    if (n.closest('.tk-fab')) continue
    if (!isVisible(n)) continue
    const b = n.getBoundingClientRect()
    if (overlapArea(box, { left: b.left, top: b.top, right: b.right, bottom: b.bottom }) > 0) return true
  }
  return false
}
function useSmartPlacement(
  btnRef: React.RefObject<HTMLButtonElement | null>,
  enabled: boolean,
  pinnedRef: React.RefObject<boolean>,
  stepsOff: boolean,
) {
  useEffect(() => {
    const btn = btnRef.current
    if (!enabled || !btn) return
    let raf = 0
    let scrollTimer = 0
    // Текущее выбранное место (null = родное). Нужно для гистерезиса: пока
    // оно свободно или почти не хуже лучшего кандидата, кнопка не двигается —
    // иначе на телефоне, где свободного места нет вовсе, она скакала по
    // экрану при каждом скролле и любой перерисовке (пользователь 2026-09-19).
    let current: Spot | null = null
    const place = () => {
      raf = 0
      const el = btnRef.current
      // Палец на кнопке (удержание, запись): не двигаем её из-под пальца.
      if (!el || pinnedRef.current) return
      // Родное место из CSS: снимаем инлайн-координаты только на время замера
      // (до отрисовки кадра дело не дойдёт — всё синхронно).
      const prevLeft = el.style.left
      const prevTop = el.style.top
      // Замер вызывает reflow, а left/top анимируются — на время замера
      // отключаем переход, иначе кнопка при каждом пересчёте прыгала бы домой
      // и плыла обратно.
      el.style.transition = 'none'
      el.style.left = ''
      el.style.top = ''
      el.style.right = ''
      el.style.bottom = ''
      const home = el.getBoundingClientRect()
      el.style.left = prevLeft
      el.style.top = prevTop
      if (prevLeft) {
        el.style.right = 'auto'
        el.style.bottom = 'auto'
      }
      void el.offsetHeight
      el.style.transition = ''
      // Размер текста в настройках — это zoom на <html>: getBoundingClientRect
      // отдаёт экранные px, а инлайн left/top на элементе внутри zoom масштабируются.
      // У самой кнопки обратный zoom (не растёт с текстом) — итог их произведение.
      const zoom = (Number(getComputedStyle(document.documentElement).zoom) || 1) * (Number(getComputedStyle(el).zoom) || 1)
      const w = home.width
      const h = home.height
      const vw = window.innerWidth
      const vh = window.visualViewport?.height ?? window.innerHeight
      const bottomSafe = Math.max(FAB_MARGIN, vh - home.bottom)
      const phone = vw < 768
      const candidates = fabCandidates({ vw, vh, w, h, home, bottomSafe, phone })
      let best = chooseFabSpot({ candidates, obstacles: collectObstacles(el, vw, vh), current, w, h, vw, vh, phone })
      // Телефон и идёт набор в поле: кнопка целиком за правым краем.
      // Низкое окно (клавиатура открыта или экран меньше ~480 px видимой
      // высоты): шар вместе с кнопкой шага забирал нижнюю треть экрана и
      // ложился на карточку слова, варианты ответа и плашки диалога
      // (ученик 15.10.2026, отчёт 113, Huawei 360×381). Прячем его так же,
      // как при наборе; вернётся, когда места снова хватит.
      // Шар выключен в настройках на экранах с заданиями: уезжает так же.
      const typing = (phone && (typingNow() || vh < FAB_LOW_VIEWPORT_PX)) || (stepsOff && stepScreenShown())
      if (typing) best = { left: vw + FAB_MARGIN, top: best.top, side: 'right' }
      // Кнопка шага внизу (урок, тренажёры, игры): шар всегда в её строке
      // слева, места не ищет. Свечение шара выше этой строки и задевало низ
      // карточки слова; обрезанная прокруткой картинка примера считалась
      // заданием, и шар уезжал на середину карточки, а на следующем шаге
      // возвращался (ученик 17.10.2026, отчёт 119, a1-l006, Huawei 360 px).
      else if (phone && bottomStepButtonShown()) best = candidates[0]
      const isHome = best === candidates[0]
      current = isHome || typing ? null : best
      // Координаты задаём инлайн всегда, и для родного места тоже: переход
      // left/top анимируется только между двумя явными значениями (из
      // CSS-«дома» с top: auto кнопка раньше прыгала рывком, пользователь
      // 2026-09-19). Родное место — замеренный home, так что визуально
      // это та же точка, что в CSS.
      el.style.left = `${Math.round(best.left / zoom)}px`
      el.style.top = `${Math.round(best.top / zoom)}px`
      el.style.right = 'auto'
      el.style.bottom = 'auto'
      el.setAttribute('data-placed', '')
      el.dataset.side = best.side
      if (typing) el.setAttribute('data-typing', '')
      else el.removeAttribute('data-typing')
      // Размер кнопки не меняем: режим «тесно» с масштабом 0.62 убран —
      // переключение туда-сюда выглядело как дёрганье (пользователь 2026-09-19).
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(place)
    }
    // По скроллу — после его окончания, а не каждый кадр: во время прокрутки
    // кнопка не должна ездить.
    const onScroll = () => {
      window.clearTimeout(scrollTimer)
      scrollTimer = window.setTimeout(schedule, 160)
    }
    schedule()
    window.addEventListener('scroll', onScroll, { capture: true, passive: true })
    // Поворот экрана / клавиатура: старые координаты могли уйти за экран —
    // начинаем с родного места.
    const onResize = () => {
      current = null
      schedule()
    }
    window.addEventListener('resize', onResize)
    window.visualViewport?.addEventListener('resize', onResize)
    // Фокус в поле ввода и обратно — уехать/вернуться сразу.
    document.addEventListener('focusin', schedule)
    document.addEventListener('focusout', schedule)
    // Свои же правки стиля кнопки — не повод пересчитывать (иначе цикл).
    // Второй замер чуть позже: новый шаг въезжает с анимацией, и по первому
    // замеру кнопки "Дальше" и варианты были ещё не на своих местах (тикет 82).
    let settleTimer = 0
    const mo = new MutationObserver((records) => {
      if (!records.some((r) => !btnRef.current?.contains(r.target))) return
      schedule()
      window.clearTimeout(settleTimer)
      settleTimer = window.setTimeout(schedule, 450)
    })
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'class', 'style'] })
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.clearTimeout(scrollTimer)
      window.clearTimeout(settleTimer)
      window.removeEventListener('scroll', onScroll, { capture: true })
      window.removeEventListener('resize', onResize)
      window.visualViewport?.removeEventListener('resize', onResize)
      document.removeEventListener('focusin', schedule)
      document.removeEventListener('focusout', schedule)
      mo.disconnect()
    }
  }, [btnRef, enabled, pinnedRef, stepsOff])
}

export function TutorFab({ variant = 'floating' }: { variant?: 'floating' | 'inline' } = {}) {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [rendered, setRendered] = useState(false)
  const [listening, setListening] = useState(false)
  const [hintVisible, setHintVisible] = useState(false)
  const [holdHint, setHoldHint] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const hintRef = useRef<HTMLSpanElement>(null)
  // Палец на кнопке: размещение (useSmartPlacement) её не двигает.
  const pinnedRef = useRef(false)
  const backdropDownRef = useRef(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const qaRef = useRef<TutorQaHandle>(null)
  const lookupRef = useRef<WordLookupHandle>(null)
  // Что показано в окне: вопросы репетитору или форма "Пожелание" /
  // "Нашёл ошибку" (.scratch/autopilot/feedback.md). Кнопки формы внизу
  // окна, чтобы написать нам можно было с любого экрана; вопросы при этом
  // остаются смонтированными и не теряются.
  const [view, setView] = useState<'qa' | FeedbackKind>('qa')
  // Гость в первом уроке (lib/guest-lesson.ts): плеер ставит data-guest-lesson с номером
  // части, и окно шара показывает вход вместо репетитора (владелец 16.10.2026).
  const [guestPart, setGuestPart] = useState<number | null>(null)
  // Гость нажал любую кнопку окна (вопрос, запись, подсказку, обращение): вместо
  // действия приглашение войти. Само окно он видит целиком, как ученик.
  // Переключатели режима (role="tab") и "Закрыть" не перехватываем: окно он смотрит
  // свободно. "Фото" ведёт гостя на публичную страницу /photo с примерами, как и
  // рекламу перевода по фото (владелец 16.10.2026); в урок он вернётся с лендинга.
  const [guestAsk, setGuestAsk] = useState<GuestReason | null>(null)
  const guestIntercept = (e: React.SyntheticEvent) => {
    if (guestPart === null) return
    const t = e.target as HTMLElement
    const action = t.closest('button, a, [role="button"]')
    if (action && (action.getAttribute('aria-label') === 'Закрыть' || action.getAttribute('role') === 'tab' || action.classList.contains('tk-pill-photo'))) return
    const enter = e.type === 'keydown' && (e as React.KeyboardEvent).key === 'Enter' && !!t.closest('input, textarea')
    if (!action && !enter) return
    e.preventDefault()
    e.stopPropagation()
    const label = action?.textContent ?? ''
    setGuestAsk(/Пожелание|Нашёл ошибку/.test(label) ? 'feedback' : 'tutor')
  }
  const readGuestPart = () => {
    const v = typeof document === 'undefined' ? null : document.querySelector('[data-guest-lesson]')?.getAttribute('data-guest-lesson')
    return v ? Number(v) || 1 : null
  }
  // Последнее поле ввода, где был фокус, — туда «Вставить» допишет слово.
  // Не только activeElement: на телефоне при наборе кнопка уезжает за край,
  // и до неё добираются, уже закрыв клавиатуру (фокус из поля ушёл).
  const lastFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const lookupTargetRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  // Поле взято без фокуса (findReplyField) — слово пойдёт в конец текста.
  const lookupAtEndRef = useRef(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Источник истины для решения «это было удержание или обычный тап» —
  // именно ref, не state: он должен быть актуален синхронно внутри самих
  // pointer-обработчиков, без ожидания следующего рендера.
  const autoListenRef = useRef(false)
  // Удержание закончилось — следом браузер пришлёт click; он не должен
  // открывать окно поверх карточки «Как сказать?».
  const swallowClickRef = useRef(false)
  const isMobile = useIsMobile()
  const coarse = useCoarsePointer()
  const desktopHover = useDesktopHover()
  const hiddenHere = variant === 'floating' && isHiddenOn(pathname)
  const { scale: textScale, fabStepsOff } = useDisplayPrefs()
  useSmartPlacement(btnRef, variant === 'floating' && !hiddenHere && !rendered, pinnedRef, fabStepsOff)
  const viewportBox = useVisualViewportBox(rendered && isMobile)
  // Размер текста в настройках — это zoom на <html> (lib/display-prefs.ts).
  // Пиксели из visualViewport и единицы dvh/vh браузер на zoom не делит:
  // оверлей с панелью выходили за низ экрана в 1,15-1,3 раза, поле ввода и
  // кнопки записи было не достать (телефон, «Крупный», 28.09). Всё, что
  // задаётся высотой экрана, делим на масштаб.
  // Видимая область заметно ниже окна — значит, открыта экранная клавиатура.
  const keyboardOpen = !!viewportBox && typeof window !== 'undefined' && viewportBox.height < window.innerHeight - 120

  // Пока окно открыто на телефоне — страница под ним не прокручивается
  // (иначе свайп по оверлею/фокус в поле уводит фон и сбивает visualViewport).
  useEffect(() => {
    if (!rendered || !isMobile) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [rendered, isMobile])

  function openModal() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setGuestPart(readGuestPart())
    setRendered(true)
    setOpen(true)
  }

  function closeModal() {
    setOpen(false)
    setGuestAsk(null)
    closeTimerRef.current = setTimeout(() => {
      setRendered(false)
      setView('qa')
    }, CLOSE_MS)
  }

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current)
  }, [])

  function clearPressTimer() {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current)
      pressTimerRef.current = null
    }
  }

  // Другие экраны (финал урока, lesson-player.tsx) открывают форму
  // "Пожелание" событием window: new CustomEvent(OPEN_FEEDBACK_EVENT).
  useEffect(() => {
    function onOpenFeedback() {
      setView('wish')
      openModal()
    }
    window.addEventListener(OPEN_FEEDBACK_EVENT, onOpenFeedback)
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, onOpenFeedback)
  }, [])

  useEffect(() => {
    function onFocusIn(e: FocusEvent) {
      const field = editableTarget(e.target as Element)
      // Поля окна репетитора не в счёт — слово нужно реплике на странице.
      if (field && !field.closest('[data-tutor-panel]')) lastFieldRef.current = field
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [])

  // Удержание сработало (порог пройден, кнопку всё ещё держат): слушаем
  // русское слово для «Как сказать?». Окно репетитора не открываем.
  function triggerAutoListen() {
    pressTimerRef.current = null
    // Гостю "Как сказать?" недоступен (нужен вход): удержание открывает окно сразу с приглашением.
    if (readGuestPart() !== null) {
      openModal()
      setGuestAsk('tutor')
      return
    }
    // Без Web Speech API удержание — просто открыть окно, как обычный тап:
    // иначе «Слушаю…» показывалось бы, ничего реально не записывая.
    if (!isVoiceInputSupported()) {
      openModal()
      return
    }
    autoListenRef.current = true
    setListening(true)
    setHoldHint(false)
    try {
      window.sessionStorage.setItem(HOLD_HINT_SESSION_KEY, '1')
    } catch {}
    lookupRef.current?.start(lookupTargetRef.current, lookupAtEndRef.current)
  }

  function handlePointerDown(e: ReactPointerEvent<HTMLButtonElement>) {
    if (e.button !== 0) return
    clearPressTimer()
    swallowClickRef.current = false
    pinnedRef.current = true
    // Захват: кнопка сжимается от :active, и палец у края оказывался «вне» её —
    // pointerleave не должен принимать это за «палец ушёл».
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {}
    // Поле со страницы, ещё живое (после перехода старое размонтировано).
    // Фокуса не было — берём реплику страницы (ответ в чате, поле упражнения).
    const last = lastFieldRef.current
    const live = last && last.isConnected ? last : null
    lookupTargetRef.current = live ?? findReplyField()
    lookupAtEndRef.current = !live
    pressTimerRef.current = setTimeout(triggerAutoListen, LONG_PRESS_MS)
  }

  // Клавиатурная активация (Enter/Space на сфокусированной кнопке) шлёт
  // только click, без pointerdown/pointerup — без этого фолбэка кнопка была
  // бы недоступна с клавиатуры. У мыши/тача click тоже прилетает следом за
  // уже обработанным pointerup: openModal() идемпотентен, повторный вызов
  // ничего не меняет и не перезапускает запись.
  function handleClick() {
    if (swallowClickRef.current) {
      swallowClickRef.current = false
      return
    }
    if (!autoListenRef.current) openModal()
  }

  function endPress() {
    pinnedRef.current = false
    if (pressTimerRef.current) {
      // Отпустили раньше порога долгого нажатия — обычный тап, просто открыть.
      clearPressTimer()
      if (!autoListenRef.current) openModal()
      return
    }
    if (autoListenRef.current) {
      autoListenRef.current = false
      swallowClickRef.current = true
      setListening(false)
      lookupRef.current?.stop()
    }
  }

  // Касание отобрали (системный жест, прокрутка): это не тап - окно не
  // открываем; если запись уже шла, останавливаем её как при отпускании.
  function cancelPress() {
    pinnedRef.current = false
    if (pressTimerRef.current) {
      clearPressTimer()
      swallowClickRef.current = true
      return
    }
    endPress()
  }

  // Считает смещение кнопка→панель и проигрывает translate+scale в нужную
  // сторону. Должен запускаться после того, как панель уже размонтирована в
  // финальном месте (rendered=true), чтобы её реальный rect был известен —
  // поэтому завязан на `rendered`, а направление (внутрь/наружу) на `open`.
  useLayoutEffect(() => {
    if (!rendered) return
    const panel = panelRef.current
    const btn = btnRef.current
    if (!panel || !btn) return

    const btnRect = btn.getBoundingClientRect()
    const panelRect = panel.getBoundingClientRect()
    // Rect'ы — в экранных px, а translate на панели внутри zoom (<html>,
    // режимы «Крупный») масштабируется: без деления окно вылетало не из кнопки.
    const zoom = Number(getComputedStyle(document.documentElement).zoom) || 1
    const dx = (btnRect.left + btnRect.width / 2 - (panelRect.left + panelRect.width / 2)) / zoom
    const dy = (btnRect.top + btnRect.height / 2 - (panelRect.top + panelRect.height / 2)) / zoom
    const collapsed = `translate(${dx}px, ${dy}px) scale(0.04)`

    if (open) {
      panel.style.transition = 'none'
      panel.style.transform = collapsed
      panel.style.opacity = '0'
      // force reflow so the collapsed state actually paints before we animate away from it
      void panel.offsetHeight
      panel.style.transition = `transform ${OPEN_MS}ms cubic-bezier(0.16, 1, 0.3, 1), opacity ${Math.min(OPEN_MS, 260)}ms ease`
      panel.style.transform = 'translate(0, 0) scale(1)'
      panel.style.opacity = '1'
    } else {
      panel.style.transition = `transform ${CLOSE_MS}ms cubic-bezier(0.4, 0, 1, 1), opacity ${CLOSE_MS}ms ease`
      panel.style.transform = collapsed
      panel.style.opacity = '0'
    }
  }, [open, rendered])

  // Esc закрывает окно; фокус в поле ввода при открытии — тот же паттерн,
  // что и у модалки «Спросить» внутри чата (ai-chat.tsx). На телефоне
  // автофокус не ставим: экранная клавиатура сразу закрывает половину окна
  // вместе с кнопками микрофона, а спрашивают там чаще голосом. Клавиатура
  // появляется только по тапу в поле — и на планшете тоже (coarse).
  useEffect(() => {
    if (!open) return
    if (!isMobile && !coarse) inputRef.current?.focus()
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') closeModal()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, isMobile, coarse])

  // Пока модалка открыта — нечего подсказывать, кнопка и так скрыта под
  // оверлеем. Таймер живёт в layout (TutorFab смонтирован один раз в
  // app/layout.tsx), поэтому цикл не перезапускается при переходах между
  // страницами внутри приложения.
  useEffect(() => {
    if (variant !== 'floating' || !coarse || !isVoiceInputSupported()) return
    try {
      if (window.sessionStorage.getItem(HOLD_HINT_SESSION_KEY)) return
      window.sessionStorage.setItem(HOLD_HINT_SESSION_KEY, '1')
    } catch {
      return
    }
    let hideTimer: ReturnType<typeof setTimeout>
    const showTimer = setTimeout(() => {
      if (bottomStepButtonShown()) return
      setHoldHint(true)
      hideTimer = setTimeout(() => setHoldHint(false), HOLD_HINT_SHOW_MS)
    }, HOLD_HINT_DELAY_MS)
    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [variant, coarse])

  // Подпись уже на экране, а шаг сменился или шар переехал, и под ней
  // оказалась кнопка: прячем сразу, не дожидаясь конца показа (тикет 82,
  // "Спроси Turkido" на "Начать проверку").
  useEffect(() => {
    if (!hintVisible) return
    const t = setInterval(() => {
      if (hintCoversControls(hintRef.current)) setHintVisible(false)
    }, 200)
    return () => clearInterval(t)
  }, [hintVisible])

  useEffect(() => {
    // Встроенный вариант всегда на виду вместе со своей постоянной подписью —
    // периодический нудж ему не нужен.
    if (variant !== 'floating' || open) return
    let showTimer: ReturnType<typeof setTimeout>
    const cycle = setInterval(() => {
      if (hintCoversControls(hintRef.current)) return
      setHintVisible(true)
      showTimer = setTimeout(() => setHintVisible(false), HINT_SHOW_MS)
    }, HINT_CYCLE_MS)
    return () => {
      clearInterval(cycle)
      clearTimeout(showTimer)
    }
  }, [open, variant])

  if (hiddenHere) return null

  return (
    <>
      {variant === 'inline' ? (
        <div className="flex flex-col items-center gap-2.5">
          <button
            ref={btnRef}
            type="button"
            onPointerDown={handlePointerDown}
            onPointerUp={endPress}
            onPointerCancel={cancelPress}
            onPointerLeave={endPress}
            onClick={handleClick}
            onContextMenu={(e) => e.preventDefault()}
            aria-label="Спроси Turkido. Тап - открыть чат, удержание - &quot;Как сказать?&quot;: скажите слово по-русски и получите турецкое"
            className={`tk-fab-inline${listening ? ' tk-fab-listening' : ''}`}
          >
            <Image src={LOGO_SRC} alt="" width={96} height={96} className="tk-fab-inline-icon" />
            {listening && <span className="tk-fab-dot animate-pulse" />}
            {desktopHover && !listening && (
              <span className="tk-fab-tip tk-fab-tip--top" aria-hidden>
                <span className="tk-fab-tip-title">
                  <TutorBrandLabel />
                </span>
                <span className="tk-fab-tip-sub">О турецком языке и жизни в Турции - текстом или голосом. Удержите кнопку - подскажу, как сказать по-турецки</span>              </span>
            )}
          </button>
          <div className="text-[15px] font-semibold" style={{ color: listening ? 'oklch(0.7 0.16 25)' : undefined }}>
            {listening ? 'Слушаю...' : <TutorBrandLabel />}
          </div>
        </div>
      ) : (
        <button
          ref={btnRef}
          type="button"
          onPointerDown={handlePointerDown}
          onPointerUp={endPress}
          onPointerCancel={cancelPress}
          onPointerLeave={endPress}
          onClick={handleClick}
          onContextMenu={(e) => e.preventDefault()}
          aria-label="Спроси Turkido. Тап - открыть чат, удержание - &quot;Как сказать?&quot;: скажите слово по-русски и получите турецкое"
          className={`tk-fab${listening ? ' tk-fab-listening' : ''}`}
        >
          <Image src={LOGO_SRC} alt="" width={96} height={96} className="tk-fab-icon" />
          {listening && <span className="tk-fab-dot animate-pulse" />}
          <span ref={hintRef} className={`tk-fab-hint pointer-events-none${!open && (listening || hintVisible || holdHint) ? ' tk-fab-hint-visible' : ''}`}>
            {listening ? 'Слушаю...' : holdHint ? 'Зажмите и скажите слово по-русски, переведу на турецкий' : <TutorBrandLabel />}
          </span>
          {desktopHover && (
            <span className="tk-fab-tip" aria-hidden>
              <span className="tk-fab-tip-title">
                <TutorBrandLabel />
              </span>
              <span className="tk-fab-tip-sub">О турецком языке и жизни в Турции - текстом или голосом. Удержите кнопку - подскажу, как сказать по-турецки</span>
            </span>
          )}
        </button>
      )}

      <WordLookup ref={lookupRef} anchorRef={btnRef} />

      {/* Портал в body: на дашборде инлайн-кнопка лежит внутри `md:sticky`
          левой колонки, а sticky-элемент сам создаёт stacking context — тогда
          z-50 работает только внутри неё, и соседняя (более поздняя в DOM)
          колонка со списком уроков перекрывает затемнение сверху. Портал
          выносит оверлей+панель на верхний уровень документа, вне зависимости
          от того, куда встроена кнопка. */}
      {rendered &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 md:items-center"
            style={
              isMobile
                ? {
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    top: (viewportBox?.top ?? 0) / textScale,
                    bottom: 'auto',
                    height: viewportBox ? viewportBox.height / textScale : `calc(100dvh / ${textScale})`,
                    display: 'flex',
                    zIndex: 50,
                  }
                : undefined
            }
            // Закрываем, только если касание и началось на фоне: при тапе по
            // спрятанной кнопке она выезжает из-под пальца, окно открывается на
            // отпускании, и следующий за ним click попадал в фон и сразу закрывал окно.
            onPointerDown={(e) => {
              backdropDownRef.current = e.target === e.currentTarget
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget && backdropDownRef.current) closeModal()
              backdropDownRef.current = false
            }}
          >
            <div
              ref={panelRef}
              data-tutor-panel
              className="flex max-h-[80vh] w-full max-w-[520px] flex-col gap-3 rounded-t-[20px] border p-4 md:rounded-[20px]"
              style={{
                background: 'oklch(0.22 0.07 258)',
                borderColor: 'var(--tk-fire)',
                transformOrigin: 'center',
                ...(isMobile
                  ? {
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      maxWidth: 'none',
                      height: '100%',
                      maxHeight: 'none',
                      minHeight: 0,
                      borderRadius: 0,
                      borderWidth: 0,
                      // Вырез/«чёлка» сверху; снизу отступ под home-индикатор —
                      // только пока клавиатура закрыта (открытая сама его перекрывает).
                      paddingTop: 'max(16px, env(safe-area-inset-top))',
                      paddingBottom: keyboardOpen ? 12 : 'max(16px, env(safe-area-inset-bottom))',
                    }
                  : // Десктоп: окно крупнее (шире и выше прежних 520px/80vh) —
                    // по просьбе пользователя, «для более лёгкого восприятия».
                    {
                      maxWidth: 780,
                      height: `min(calc(84vh / ${textScale}), 820px)`,
                      maxHeight: 'none',
                      padding: '22px 26px',
                    }),
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* На десктопе всё содержимое окна (шапка, подсказки, поле, кнопки
                  записи, карточки ответов) пропорционально увеличено одним
                  коэффициентом через `zoom`, а не десятками правок px-размеров
                  в tutor-qa.tsx и qa-answer-card.tsx. Обёртка — flex-колонка
                  на всю высоту панели, иначе список ответов не получит скролл. */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  flex: 1,
                  minHeight: 0,
                  ...(isMobile ? null : { zoom: 1.2 }),
                }}
              >
              <div className="flex flex-shrink-0 items-center justify-between">
                <div className="flex items-center gap-2">
                  <Image src={LOGO_SRC} alt="" width={22} height={22} />
                  <div className="text-[16px] font-semibold">
                    <TutorBrandLabel />
                  </div>
                </div>
                <button type="button" className="tk-back-btn" aria-label="Закрыть" onClick={closeModal}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              </div>

              {/* Вопросы остаются в DOM и под формой обратной связи: иначе
                  переключение стирало бы полученные ответы. display: contents
                  не ломает flex-колонку (дети TutorQa остаются её элементами). */}
              {guestAsk && guestPart !== null ? (
                <GuestTutorPrompt part={guestPart} reason={guestAsk} />
              ) : (
                <div style={{ display: 'contents' }} onClickCapture={guestIntercept} onPointerDownCapture={guestIntercept} onKeyDownCapture={guestIntercept}>
              <div style={{ display: view === 'qa' ? 'contents' : 'none' }}>
                <TutorQa ref={qaRef} inputRef={inputRef} onLeave={closeModal} />
              </div>
              {view === 'qa' ? (
                <div className="flex flex-shrink-0 flex-wrap items-center gap-2" role="group" aria-label="Написать нам">
                  {/* Без подписи «Написать нам:»: с ней на крупном тексте пилюли
                      переносились на вторую строку. */}
                  <button type="button" className="tk-pill-toggle" onClick={() => setView('wish')}>
                    Пожелание
                  </button>
                  <button type="button" className="tk-pill-toggle" onClick={() => setView('bug')}>
                    Нашёл ошибку
                  </button>
                </div>
              ) : (
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingTop: 4 }}>
                  <FeedbackForm initialKind={view} onBack={() => setView('qa')} />
                </div>
              )}
                </div>
              )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
